/**
 * Two-slot file requirements panel for the Adesa Run List workflow.
 *
 * Direct sibling of components/wholesale/FileRequirementsPanel.tsx (the
 * Cleared List version). Same chrome, same drop-zone behavior, same
 * status glyphs — adapted for the auction shape (Adesa Run List + VAUTO).
 *
 * Duplicated by design rather than parameterized — the Cleared List
 * panel is shipped/tested and a generalization would require a small
 * refactor of working code. When a third workflow lands, revisit
 * consolidation as its own scoped commit.
 */
import React, { useEffect, useRef, useState } from "react"
import {
  ChevronDown,
  ChevronRight,
  FolderUp,
  Loader2,
  XCircle,
} from "lucide-react"

import {
  AuctionFileSummary,
  AuctionSession,
  AuctionVendor,
} from "../../api/wholesaleAuction"
import { THEME } from "../../config/theme"
import { useWholesaleAuction } from "../../context/WholesaleAuctionContext"
import { formatAge } from "../../utils/formatAge"

interface Props {
  session: AuctionSession | null
  // True when a run is currently loaded on screen. When viewing a run we don't
  // auto-open the upload panel (you're not building a new run), so a midnight
  // cleanup of yesterday's uploads doesn't pop it open on an old run.
  runLoaded: boolean
}

interface SlotDef {
  vendor: AuctionVendor
  label: string
}

// The two single-file slots. Rapid Recon is the third requirement but a
// MULTI-file slot (one per rooftop), rendered by its own section below.
const SLOTS: SlotDef[] = [
  { vendor: "adesa_run_list", label: "Adesa Run List" },
  { vendor: "vauto",          label: "VAUTO Export" },
]

interface UploadingState {
  filename: string
  status: "uploading" | "error"
  error?: string
}

export default function AuctionFileRequirementsPanel({ session, runLoaded }: Props) {
  // Three logical requirements: Adesa, VAUTO (single files), and Rapid Recon
  // (multi-file — satisfied only when all six rooftops are covered).
  const adesaReady = !!session?.adesa_run_list
  const vautoReady = !!session?.vauto
  const rrReady = !!session?.rapidrecon_ready
  const allReady = adesaReady && vautoReady && rrReady
  const missing =
    (adesaReady ? 0 : 1) + (vautoReady ? 0 : 1) + (rrReady ? 0 : 1)

  // Auto-open only matters when you're building a run (no run on screen): open
  // while a required file is missing, collapse once both are in. While viewing
  // an existing run we stay collapsed — a midnight cleanup of yesterday's
  // uploads must not pop the panel open on an old run. Manual toggles are
  // respected between state changes (we only re-sync on an actual transition).
  const autoExpanded = !runLoaded && !allReady

  const [expanded, setExpanded] = useState(autoExpanded)
  const lastSyncRef = useRef({ allReady, runLoaded })
  useEffect(() => {
    const prev = lastSyncRef.current
    if (prev.allReady !== allReady || prev.runLoaded !== runLoaded) {
      lastSyncRef.current = { allReady, runLoaded }
      setExpanded(!runLoaded && !allReady)
    }
  }, [allReady, runLoaded])

  const [uploading, setUploading] = useState<UploadingState[]>([])

  return (
    <div
      style={{
        border: `1px solid ${THEME.colors.silver}`,
        borderRadius: THEME.borderRadius.md,
        backgroundColor: THEME.colors.neutral.light.surface,
        overflow: "hidden",
        fontFamily: THEME.fonts.primary,
      }}
    >
      <Header
        expanded={expanded}
        missing={missing}
        allReady={allReady}
        onToggle={() => setExpanded((v) => !v)}
      />

      {expanded && (
        <div style={{ padding: `0 ${THEME.spacing.md} ${THEME.spacing.md}` }}>
          <SharedDropZone uploading={uploading} setUploading={setUploading} />

          <SectionHeading>Required</SectionHeading>

          <div>
            {SLOTS.map((slot) => (
              <SlotRow
                key={slot.vendor}
                slot={slot}
                file={session ? sessionFileFor(session, slot.vendor) : null}
                uploading={uploading.find(
                  (u) => guessVendorFromFilename(u.filename) === slot.vendor,
                )}
              />
            ))}
          </div>

          <RapidReconSection session={session} />
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Rapid Recon — multi-file slot (one per-rooftop export). The requirement is
// satisfied only when all six rooftops are covered; we surface each rooftop's
// ✓ / ○ plus the list of uploaded files (each removable).
// ---------------------------------------------------------------------------

function RapidReconSection({ session }: { session: AuctionSession | null }) {
  const { deleteFile } = useWholesaleAuction()
  const files = session?.rapidrecon ?? []
  const expected = session?.rapidrecon_rooftops_expected ?? []
  const covered = new Set(session?.rapidrecon_rooftops_covered ?? [])
  const ready = !!session?.rapidrecon_ready

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginTop: THEME.spacing.md,
          marginBottom: THEME.spacing.sm,
        }}
      >
        <span
          style={{
            fontSize: THEME.typography.caption.size,
            fontWeight: THEME.typography.caption.weight,
            letterSpacing: THEME.typography.caption.tracking,
            textTransform: "uppercase",
            color: THEME.colors.neutral.light.textSecondary,
          }}
        >
          RapidRecon — one per rooftop
        </span>
        <span
          style={{
            fontSize: 12,
            color: ready
              ? THEME.colors.semantic.success
              : THEME.colors.semantic.warning,
          }}
        >
          {covered.size} of {expected.length} rooftops
        </span>
      </div>

      {/* The six rooftops with a covered / missing indicator. */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: THEME.spacing.xs,
          marginBottom: THEME.spacing.sm,
        }}
      >
        {expected.map((rooftop) => {
          const isCovered = covered.has(rooftop)
          return (
            <span
              key={rooftop}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 8px",
                borderRadius: THEME.borderRadius.sm,
                fontSize: 12,
                border: `1px solid ${
                  isCovered ? THEME.colors.semantic.success : THEME.colors.silver
                }`,
                color: isCovered
                  ? THEME.colors.semantic.success
                  : THEME.colors.neutral.light.textSecondary,
                background: isCovered ? "rgba(34, 139, 58, 0.06)" : "transparent",
              }}
            >
              <span style={{ fontWeight: 600 }}>{isCovered ? "✓" : "○"}</span>
              {rooftop}
            </span>
          )
        })}
      </div>

      {/* Uploaded Rapid Recon files. */}
      {files.length === 0 ? (
        <div
          style={{
            fontSize: 12,
            fontStyle: "italic",
            color: THEME.colors.semantic.warning,
            padding: "2px 0",
          }}
        >
          No RapidRecon files uploaded yet — drop all six rooftop exports above.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {files.map((file) => (
            <RrFileRow key={file.file_id} file={file} onRemove={() => deleteFile(file.file_id)} />
          ))}
        </div>
      )}
    </>
  )
}

function RrFileRow({
  file,
  onRemove,
}: {
  file: AuctionFileSummary
  onRemove: () => void
}) {
  const rooftops = (file.rooftops_covered ?? []).join(", ")
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: THEME.spacing.sm,
        padding: "3px 0",
        fontSize: 13,
        minHeight: 22,
      }}
    >
      <span
        style={{
          width: 18,
          textAlign: "center",
          flexShrink: 0,
          color: THEME.colors.semantic.success,
          fontWeight: 600,
        }}
      >
        ✓
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: THEME.colors.neutral.light.textSecondary,
        }}
      >
        {file.filename} — {file.row_count.toLocaleString()} rows
        {rooftops ? ` • ${rooftops}` : ""}
      </span>
      <button
        type="button"
        onClick={onRemove}
        title="Remove this file"
        style={{
          flexShrink: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          color: THEME.colors.neutral.light.textSecondary,
          display: "inline-flex",
          alignItems: "center",
          padding: 2,
        }}
      >
        <XCircle size={14} />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

interface HeaderProps {
  expanded: boolean
  missing: number
  allReady: boolean
  onToggle: () => void
}

function Header({ expanded, missing, allReady, onToggle }: HeaderProps) {
  const summaryText = allReady
    ? "all ready"
    : `${missing} required file${missing === 1 ? "" : "s"} missing`
  const summaryColor = allReady
    ? THEME.colors.semantic.success
    : THEME.colors.semantic.warning

  return (
    <div
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        gap: THEME.spacing.sm,
        padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
        cursor: "pointer",
        userSelect: "none",
        fontSize: 13,
        fontWeight: 600,
        color: THEME.colors.neutral.light.textPrimary,
      }}
    >
      {expanded ? (
        <ChevronDown
          size={14}
          strokeWidth={2}
          style={{ color: THEME.colors.neutral.light.textSecondary }}
        />
      ) : (
        <ChevronRight
          size={14}
          strokeWidth={2}
          style={{ color: THEME.colors.neutral.light.textSecondary }}
        />
      )}
      File Requirements
      <span
        style={{
          fontWeight: 400,
          fontSize: 12,
          marginLeft: 4,
          color: summaryColor,
        }}
      >
        — {summaryText}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared drop zone
// ---------------------------------------------------------------------------

interface DropZoneProps {
  uploading: UploadingState[]
  setUploading: React.Dispatch<React.SetStateAction<UploadingState[]>>
}

function SharedDropZone({ uploading, setUploading }: DropZoneProps) {
  const { uploadFile } = useWholesaleAuction()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dragOver, setDragOver] = useState(false)

  const beginUploads = async (files: File[]) => {
    for (const incoming of files) {
      setUploading((prev) => [
        ...prev,
        { filename: incoming.name, status: "uploading" },
      ])
      try {
        await uploadFile(incoming)
        setUploading((prev) =>
          prev.filter((u) => u.filename !== incoming.name),
        )
      } catch (err) {
        const message = err instanceof Error ? err.message : "Upload failed"
        setUploading((prev) =>
          prev.map((u) =>
            u.filename === incoming.name
              ? { ...u, status: "error", error: message }
              : u,
          ),
        )
        setTimeout(() => {
          setUploading((prev) =>
            prev.filter((u) => u.filename !== incoming.name),
          )
        }, 4000)
      }
    }
  }

  const handleClick = () => inputRef.current?.click()

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const list = event.target.files
    if (list && list.length > 0) beginUploads(Array.from(list))
    event.target.value = ""
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragOver(false)
    const list = event.dataTransfer.files
    if (list && list.length > 0) beginUploads(Array.from(list))
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragOver(true)
  }

  return (
    <div>
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragOver(false)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: THEME.spacing.sm,
          padding: `${THEME.spacing.md} ${THEME.spacing.md}`,
          border: `1.5px dashed ${dragOver ? THEME.colors.navy : THEME.colors.silver}`,
          borderRadius: THEME.borderRadius.md,
          background: dragOver
            ? "rgba(13, 26, 66, 0.06)"
            : THEME.colors.neutral.light.background,
          cursor: "pointer",
          fontSize: 13,
          color: THEME.colors.neutral.light.textSecondary,
          transition: "background 120ms ease-out, border-color 120ms ease-out",
        }}
      >
        <FolderUp size={16} strokeWidth={1.75} />
        <span>Drag files here or click to browse</span>
      </div>

      {uploading.length > 0 && (
        <div
          style={{
            marginTop: THEME.spacing.sm,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {uploading.map((u) => (
            <div
              key={u.filename}
              style={{
                display: "flex",
                alignItems: "center",
                gap: THEME.spacing.sm,
                fontSize: 12,
                color:
                  u.status === "error"
                    ? THEME.colors.semantic.danger
                    : THEME.colors.neutral.light.textSecondary,
              }}
            >
              {u.status === "uploading" ? (
                <Loader2
                  size={14}
                  className="invi-spin"
                  style={{ color: THEME.colors.navy }}
                />
              ) : (
                <XCircle size={14} color={THEME.colors.semantic.danger} />
              )}
              <span style={{ fontWeight: 500 }}>{u.filename}</span>
              {u.status === "uploading" && <span>uploading…</span>}
              {u.status === "error" && <span>{u.error ?? "upload failed"}</span>}
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        multiple
        style={{ display: "none" }}
        onChange={handleFileInput}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// REQUIRED section + per-slot rows
// ---------------------------------------------------------------------------

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: THEME.typography.caption.size,
        fontWeight: THEME.typography.caption.weight,
        letterSpacing: THEME.typography.caption.tracking,
        textTransform: "uppercase",
        color: THEME.colors.neutral.light.textSecondary,
        marginTop: THEME.spacing.md,
        marginBottom: THEME.spacing.sm,
      }}
    >
      {children}
    </div>
  )
}

interface SlotRowProps {
  slot: SlotDef
  file: AuctionFileSummary | null
  uploading: UploadingState | undefined
}

function SlotRow({ slot, file, uploading }: SlotRowProps) {
  const populated = file !== null && !uploading
  const isUploading = !!uploading
  const isMissing = !populated && !isUploading

  const { symbol, color } = pickGlyph({ uploading: isUploading, populated })

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: THEME.spacing.sm,
        padding: "3px 0",
        minHeight: 22,
        fontSize: 13,
      }}
    >
      <span
        className={isUploading ? "invi-spin" : undefined}
        style={{
          width: 18,
          textAlign: "center",
          flexShrink: 0,
          color,
          fontWeight: 600,
          fontSize: 14,
        }}
      >
        {symbol}
      </span>
      <span
        style={{
          width: 200,
          flexShrink: 0,
          fontWeight: 500,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        {slot.label}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: isMissing
            ? color
            : THEME.colors.neutral.light.textSecondary,
          fontStyle: isMissing ? "italic" : "normal",
        }}
      >
        {renderDetail({ uploading, file })}
      </span>
    </div>
  )
}

function pickGlyph({
  uploading,
  populated,
}: {
  uploading: boolean
  populated: boolean
}): { symbol: string; color: string } {
  if (uploading) return { symbol: "↻", color: THEME.colors.navy }
  if (populated) return { symbol: "✓", color: THEME.colors.semantic.success }
  return { symbol: "○", color: THEME.colors.neutral.light.textSecondary }
}

interface DetailArgs {
  uploading: UploadingState | undefined
  file: AuctionFileSummary | null
}

function renderDetail({ uploading, file }: DetailArgs): string {
  if (uploading) {
    return uploading.status === "error"
      ? (uploading.error ?? "upload failed")
      : "uploading…"
  }
  if (file) {
    const ageLabel = ageFromIso(file.uploaded_at)
    const rowsLabel = `${file.row_count.toLocaleString()} rows`
    return `${file.filename} — ${rowsLabel}${ageLabel ? ` • ${ageLabel}` : ""}`
  }
  return "not uploaded"
}

function ageFromIso(uploadedAt: string | null): string {
  if (!uploadedAt) return ""
  const ms = Date.now() - new Date(uploadedAt).getTime()
  if (Number.isNaN(ms) || ms < 0) return ""
  return formatAge(Math.floor(ms / 1000))
}

function sessionFileFor(
  session: AuctionSession,
  vendor: AuctionVendor,
): AuctionFileSummary | null {
  return vendor === "adesa_run_list" ? session.adesa_run_list : session.vauto
}

// Best-effort guess of which slot an in-flight upload will land in.
// Backend is the authoritative detector; this is purely so the row in
// the list shows a spinner near the right slot while the request is in
// flight. The .xls / .xlsx form is VAUTO's typical export; the .csv form
// is the Adesa run list.
function guessVendorFromFilename(filename: string): AuctionVendor | null {
  const lower = filename.toLowerCase()
  if (lower.endsWith(".xls") || lower.endsWith(".xlsx")) return "vauto"
  if (lower.endsWith(".csv")) return "adesa_run_list"
  return null
}
